"use client";

/**
 * Confirm-remove-candidate Modal (spec §6.2.5 — `IconCircleMinus`).
 *
 * Shown when a recruiter clicks the trash button on a recipient card.
 * Removal calls `usePatchPreview({ remove_job_candidate_ids })`
 * upstream — this component just owns the copy + Cancel/Remove
 * buttons.
 */

import { Button, SubmitButton } from "@/components/ui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";

export interface RemoveCandidateConfirmProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  candidateName: string | null;
  onConfirm: () => Promise<void> | void;
  isPending?: boolean;
}

export function RemoveCandidateConfirm({
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: RemoveCandidateConfirmProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="sm">
        <ModalHeader
          title="Remove candidate from this enrollment?"
          iconName="IconCircleMinus"
        />
        <ModalBody>
          <p className="text-sm text-muted-foreground">
            Candidate will stay on this job&apos;s pipeline. Only the pending
            preview drops them. Re-open a fresh preview to add them back.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <SubmitButton
            type="button"
            variant="destructive"
            size="sm"
            loading={Boolean(isPending)}
            loadingLabel="Remove"
            onClick={onConfirm}
          >
            Remove
          </SubmitButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
